import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { PENDING_ORG_INVITE_TOKEN_KEY } from '../constants/orgInvite'
import { loadContinuation, boundedContext, clearContinuation, appendContinuation } from '../context/oauthContinuation'
import { resolvePostVerificationDestination } from '../utils/postVerificationRedirect'

const CHAT_FIRST_SETUP_ENABLED = import.meta.env.VITE_CHAT_FIRST_SETUP_ENABLED === 'true'

/**
 * Email verification, as pages/VerifyEmailPage.jsx does it, for the customer build's Control Board-styled page: the same
 * verify and resend calls, the 60-second resend wait, and the same destination afterwards (an invitation, a pending AI
 * assistant authorization, or home), with the invitation token and the continuation handled exactly as there.
 * Keep the two in step.
 */
export function useVerifyEmailFlow() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [countdown, setCountdown] = useState(60)
  const [resending, setResending] = useState(false)
  const { user, verifyEmail, resendOtp } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (countdown <= 0) return
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [countdown])

  /** Verify the 6-digit code. Resolves true on success (the page then moves on), false on a refusal. */
  const verify = useCallback(async (otp) => {
    if (otp.length !== 6) return false
    setLoading(true)
    setError('')
    try {
      await verifyEmail(otp)
      let pendingInviteToken = null
      try {
        pendingInviteToken = localStorage.getItem(PENDING_ORG_INVITE_TOKEN_KEY)?.trim()
      } catch {
        /* ignore */
      }
      const fromUrl = boundedContext(typeof window !== 'undefined' ? window.location.search : '')
      const pendingOAuthContext = fromUrl || loadContinuation()
      const dest = resolvePostVerificationDestination({
        pendingInviteToken,
        pendingOAuthContext,
        chatFirstSetupEnabled: CHAT_FIRST_SETUP_ENABLED,
        nextSetupPath: user?.nextSetupPath,
      })
      const { clearInviteToken, clearOAuthContext } = dest
      let path = dest.path
      if (pendingOAuthContext && fromUrl && path.startsWith('/join')) {
        path = appendContinuation(path, pendingOAuthContext)
      }
      if (clearInviteToken && pendingInviteToken) {
        try {
          localStorage.removeItem(PENDING_ORG_INVITE_TOKEN_KEY)
        } catch {
          /* ignore */
        }
      }
      if (clearOAuthContext) clearContinuation()
      navigate(path, { replace: true })
      return true
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.')
      return false
    } finally {
      setLoading(false)
    }
  }, [verifyEmail, navigate, user?.nextSetupPath])

  const resend = async () => {
    if (countdown > 0 || resending) return
    setResending(true)
    setError('')
    try {
      await resendOtp()
      setCountdown(60)
    } catch (err) {
      setError(err.message || 'Failed to resend code.')
    } finally {
      setResending(false)
    }
  }

  return { email: user?.email ?? null, verify, resend, loading, error, countdown, resending }
}
